import { ChangeDetectionStrategy, Component, EventEmitter, Input, Output } from '@angular/core';

import { ModalButtons, NotificationScale, NotificationTheme } from '@fcom/ui-components';
import { ServiceCatalogService } from '@fcom/dapi/interfaces/services.interface';

import {
  getServiceContentHtml,
  getServiceContentUrlKey,
  getServiceDescriptionText,
  getServiceNotificationKey,
} from '../../utils/bundles.utils';

type ProductInfoView = {
  serviceInfo: ServiceCatalogService;
  descriptionText: string | null;
  notificationKey: string | null;
  contentUrlKey: string | null;
  contentHtml: string | null;
};

/**
 * Presentational modal that shows catalog info (image, description, notification, CMS/inline content) for a
 * single ancillary service. It is deliberately store-agnostic: callers pass an already-resolved service, so
 * the same modal can be reused anywhere a service needs a details view (seat bundle promotions,
 * ancillary+ancillary combo cards, travel-extras cards, ...). Resolution of *which* service to show (e.g.
 * from a seat bundle category) belongs to the caller / a selector, not here.
 */
@Component({
  selector: 'fin-product-info-modal',
  templateUrl: './product-info-modal.component.html',
  styleUrls: ['./product-info-modal.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class ProductInfoModalComponent {
  readonly ModalButtons = ModalButtons;
  readonly NotificationScale = NotificationScale;
  readonly NotificationTheme = NotificationTheme;

  @Input()
  open = false;

  /** The already-resolved catalog service to show. */
  @Input()
  serviceInfo: ServiceCatalogService | null = null;

  @Output()
  closeModal: EventEmitter<void> = new EventEmitter();

  get productInfo(): ProductInfoView | null {
    if (!this.serviceInfo) {
      return null;
    }
    const { category } = this.serviceInfo;
    return {
      serviceInfo: this.serviceInfo,
      descriptionText: getServiceDescriptionText(category, this.serviceInfo),
      notificationKey: getServiceNotificationKey(category),
      contentUrlKey: getServiceContentUrlKey(category, this.serviceInfo),
      contentHtml: getServiceContentHtml(category, this.serviceInfo),
    };
  }
}
